// src/vlp/components/common/ClientSelectorDialog.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  CircularProgress,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
  Paper,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClients,
  selectClientsData,
  selectClientsStatus,
  selectClientsError,
} from "../../../redux/clientsSlice";

const ClientSelectorDialog = ({
  open,
  onClose,
  onSelect,
  multiSelect = false,
}) => {
  const dispatch = useDispatch();
  const clientsFromStore = useSelector(selectClientsData);
  const clientsStatus = useSelector(selectClientsStatus);
  const clientsError = useSelector(selectClientsError);

  const [search, setSearch] = useState("");
  const [selectedCodes, setSelectedCodes] = useState([]);
  const searchInputRef = useRef(null);

  // Normalize - Updated for ClientsSearchDto structure (camelCase)
  const getAccountNumber = (c) => c?.accountNumber ?? "";
  const getClientName = (c) => c?.clientName ?? "";
  const getStatus = (c) => c?.status ?? "";
  const getCounty = (c) => c?.county ?? "";
  const getClientType = (c) => c?.clientType ?? "";

  // Load data
  useEffect(() => {
    if (open && clientsStatus === "idle") {
      dispatch(fetchClients());
    }
  }, [open, clientsStatus, dispatch]);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedCodes([]);
      setSearch("");
      // Focus the search box after a short delay to ensure Dialog is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Filter first, then limit to 20
  const filteredClients = useMemo(() => {
    let list = clientsFromStore || [];

    // Apply search filter first
    if (search.trim()) {
      const term = search.toLowerCase().trim();

      const originalList = [...list]; // Keep original for comparison

      list = list.filter((c) => {
        const accountNumber = getAccountNumber(c).toLowerCase();
        const clientName = getClientName(c).toLowerCase();
        const status = getStatus(c).toLowerCase();
        const county = getCounty(c).toLowerCase();
        const clientType = getClientType(c).toLowerCase();

        const matches =
          accountNumber.includes(term) ||
          clientName.includes(term) ||
          status.includes(term) ||
          county.includes(term) ||
          clientType.includes(term);

        return matches;
      });
    } else {
      console.log("No search term - showing all clients");
    }

    // Then limit to 20 results (whether filtered or not)
    const finalList = list.slice(0, 20);

    return finalList;
  }, [clientsFromStore, search]);

  const toggleSelection = (code) => {
    if (!multiSelect) {
      setSelectedCodes([code]);
      // Auto-select and close for single select
      const client = (clientsFromStore || []).find(
        (c) => getAccountNumber(c) === code
      );
      onSelect(client || null);
      onClose();
      return;
    }
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleConfirm = () => {
    if (multiSelect) {
      const selectedClients = (clientsFromStore || []).filter((c) =>
        selectedCodes.includes(getAccountNumber(c))
      );
      onSelect(selectedClients);
    } else {
      const client = (clientsFromStore || []).find(
        (c) => getAccountNumber(c) === selectedCodes[0]
      );
      onSelect(client || null);
    }
    onClose();
  };

  const removeSelectedCode = (codeToRemove) => {
    setSelectedCodes((prev) => prev.filter((code) => code !== codeToRemove));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "60vh",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          background: "linear-gradient(135deg, #1871CA 0%, #1871CA 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <BusinessIcon />
        {multiSelect ? "Select Clients" : "Select Client"}
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {clientsError && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "error.lighter",
              border: "1px solid",
              borderColor: "error.light",
              borderRadius: 2,
            }}
          >
            <Typography color="error" variant="body2">
              {clientsError}
            </Typography>
          </Paper>
        )}

        <TextField
          inputRef={searchInputRef} 
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by client code, name, account number, status, or county..."
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "grey.50",
              height: "40px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Debug info - remove this after testing */}
        {search.trim() && (
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, color: "warning.main" }}
          >
            Searching for "{search}" - Found {filteredClients.length} results
          </Typography>
        )}

        {/* Selected items chips for multiSelect */}
        {multiSelect && selectedCodes.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Selected ({selectedCodes.length}):
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedCodes.map((code) => (
                <Chip
                  key={code}
                  label={code}
                  onDelete={() => removeSelectedCode(code)}
                  deleteIcon={<CloseIcon />}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {clientsStatus === "loading" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                Loading clients...
              </Typography>
            </Box>
          </Box>
        ) : filteredClients.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6">No results found</Typography>
            <Typography variant="body2">
              Try adjusting your search terms
            </Typography>
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              maxHeight: 400,
              overflowY: "auto",
            }}
          >
            <List dense sx={{ p: 0 }}>
              {filteredClients.map((c, index) => {
                const code = getAccountNumber(c);
                const clientName = getClientName(c);
                const isSelected = selectedCodes.includes(code);

                return (
                  <React.Fragment key={code}>
                    <ListItem
                      disablePadding
                      sx={{
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={() => toggleSelection(code)}
                        sx={{
                          py: 1,
                          px: 2,
                          minHeight: "48px",
                          ...(isSelected && {
                            bgcolor: "success.lighter",
                            "&:hover": {
                              bgcolor: "success.light",
                            },
                          }),
                        }}
                      >
                        {multiSelect && (
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                              color="primary"
                              size="small"
                            />
                          </ListItemIcon>
                        )}
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 2,
                            bgcolor: isSelected ? "success.main" : "grey.400",
                            fontSize: "0.75rem",
                          }}
                        >
                          {clientName.substring(0, 2).toUpperCase() || "CL"}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: isSelected ? 700 : 600,
                                  color: isSelected
                                    ? "success.main"
                                    : "#1976d2",
                                  fontSize: "0.9rem",
                                }}
                              >
                                {code}
                              </Typography>
                              {getStatus(c) && (
                                <Chip
                                  label={getStatus(c)}
                                  size="small"
                                  color={getStatusColor(getStatus(c))}
                                  variant="filled"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.65rem",
                                    fontWeight: 600,
                                    "& .MuiChip-label": {
                                      px: 1,
                                    },
                                  }}
                                />
                              )}
                              {getClientType(c) && (
                                <Chip
                                  label={getClientType(c)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.65rem",
                                    fontWeight: 500,
                                    borderColor: "#9c27b0",
                                    color: "#9c27b0",
                                    "& .MuiChip-label": {
                                      px: 1,
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 0.5,
                                  fontWeight: 600,
                                  color: isSelected ? "#2e7d32" : "#1a1a1a",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {clientName}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  flexWrap: "wrap",
                                }}
                              >
                                {getAccountNumber(c) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#666",
                                        fontWeight: 500,
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      Account:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#f57c00",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {getAccountNumber(c)}
                                    </Typography>
                                  </Box>
                                )}
                                {getCounty(c) && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#666",
                                        fontWeight: 500,
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      County:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#7b1fa2",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {getCounty(c)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredClients.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}

        {filteredClients.length === 20 && search.trim() === "" && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block", textAlign: "center" }}
          >
            Showing first 20 results. Use search to narrow down.
          </Typography>
        )}
        {filteredClients.length === 20 && search.trim() !== "" && (
          <Typography
            variant="caption"
            color="warning.main"
            sx={{
              mt: 1,
              display: "block",
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            {filteredClients.length}+ results found. Use more specific search
            terms to narrow down.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        {multiSelect && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={selectedCodes.length === 0}
            sx={{
              borderRadius: 2,
              px: 3,
              background: "linear-gradient(45deg, #2e7d32 30%, #388e3c 90%)",
            }}
          >
            Select ({selectedCodes.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClientSelectorDialog;
